import { formatDate } from '@seen/core/dates'
import { CommentCommunication } from '@seen/core/models/communications/CommentCommunication'
import { SDR } from '@seen/core/models/tasks/sdr/sdr'
import { SDRStatus } from '@seen/core/models/tasks/sdr/utils'
import { orderBy, sortBy } from 'lodash-es'
import type { JSX, ReactNode } from 'react'
import { ActivityComment } from '#src/components/activity/ActivityComment.tsx'
import { ActivityEntry } from '#src/components/activity/ActivityEntry.tsx'
import { useVersionOriginTask } from '#src/components/activity/useVersionOriginTask.ts'
import { useResourceVersions } from '#src/hooks/useResourceVersions.ts'
import { useSearch } from '#src/hooks/useSearch.ts'

type SDRActivityEntry = {
  action: ReactNode
  sdr: SDR
  key?: string
}

export function SDRActivity({ sdr }: { sdr: SDR }): JSX.Element {
  const { data: pastSDRs = [] } = useResourceVersions(SDR, sdr.id)
  const sortedSDRs = sortBy(pastSDRs, (s: SDR) => s.lastUpdated)
  const sdrOriginOf = useVersionOriginTask(pastSDRs)

  const sdrActivity = sortedSDRs.reduce<SDRActivityEntry[]>(
    (acc, curr: SDR) => {
      if (acc.length === 0) {
        acc.push({
          action: 'SDR created',
          sdr: curr,
          key: `${curr.versionId}-created`,
        })
        return acc
      }

      const prev = acc[acc.length - 1]

      if (curr.status !== prev.sdr.status) {
        let actionText = ''
        switch (curr.status) {
          case SDRStatus.IN_REVIEW:
            actionText = 'SDR advanced to In Review'
            break
          case SDRStatus.APPROVED:
            actionText = 'SDR approved'
            break
          case SDRStatus.DENIED:
            actionText = 'SDR denied'
            break
          case SDRStatus.PARTIALLY_APPROVED:
            actionText = 'SDR partially approved'
            break
          case SDRStatus.PARTIALLY_DENIED:
            actionText = 'SDR partially denied'
            break
          case SDRStatus.IN_APPEAL:
            actionText = 'SDR moved to appeal'
            break
          case SDRStatus.WITHDRAWN:
            actionText = 'SDR withdrawn'
            break
          default:
            actionText = `SDR status changed to ${curr.status}`
        }

        acc.push({
          action: actionText,
          sdr: curr,
          key: `${curr.versionId}-status-change`,
        })
        return acc
      }

      if (curr.notificationDate && !prev.sdr.notificationDate) {
        acc.push({
          action: `SDR notification sent on ${formatDate(curr.notificationDate)}`,
          sdr: curr,
          key: `${curr.versionId}-notified`,
        })
        return acc
      }

      const prevDueDate = prev.sdr.decisionNotificationDate?.toISOString()
      const currDueDate = curr.decisionNotificationDate?.toISOString()
      if (currDueDate && prevDueDate !== currDueDate) {
        const fromLabel = prevDueDate ? formatDate(prevDueDate) : 'not set'
        acc.push({
          action: `Due date revised: ${fromLabel} → ${formatDate(currDueDate)}`,
          sdr: curr,
          key: `${curr.versionId}-due-date-change`,
        })
        return acc
      }

      return acc
    },
    []
  )

  const { data: comments = [] } = useSearch(
    CommentCommunication,
    CommentCommunication.searchQuery([sdr.reference])
  )

  const all = orderBy(
    [...sdrActivity, ...comments],
    (a) => {
      if (a instanceof CommentCommunication) {
        return a.sent
      }
      return a.sdr.lastUpdated
    },
    ['desc']
  )

  return (
    <>
      {all.map((entry) => {
        if (entry instanceof CommentCommunication) {
          return (
            <ActivityComment key={entry.reference.reference} comment={entry} />
          )
        }
        return (
          <ActivityEntry
            key={entry.key ?? entry.sdr.versionId}
            author={entry.sdr.lastUpdatedBy}
            time={entry.sdr.lastUpdated}
            action={entry.action}
            originTask={sdrOriginOf(entry.sdr)}
          />
        )
      })}
    </>
  )
}
